import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Download, Printer } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getImportBookingDetails } from '@/redux/ImportBooking/actions';

const valueOrDash = (value) => (value != null && String(value).trim() !== '' ? value : '-');

const pick = (obj, keys) => {
    for (const key of keys) {
        const value = obj?.[key];
        if (value != null && String(value).trim() !== '') return value;
    }
    return '';
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).isValid() ? moment(dateString).format('DD MMM YYYY') : '';
};

const cleanPort = (value) => {
    if (!value) return '';
    return String(value).split('[')[0].trim();
};

const normalizeCargoRows = (data) => {
    const rows = data?.ContainersCargoModel || data?.BOLDescriptionsData || [];
    if (!Array.isArray(rows) || rows.length === 0) {
        return [{
            marksAndNumbers: '-',
            numberOfPackages: '-',
            description: '-',
            grossWeight: '-',
            measurement: '-',
        }];
    }

    return rows.map((item) => {
        const marks = pick(item, ['MARKS_NO', 'MarksNos']);
        const containerNo = pick(item, ['CONTAINER_NUMBER', 'ContainerNumber']);
        const seal = pick(item, ['SEAL_NUMBER', 'SealNumber']);
        const contSize = pick(item, ['CONT_SIZE', 'ContSize']);
        const contType = pick(item, ['CONT_TYPE', 'ContTypeName']);
        const pkg = pick(item, ['NO_PACKAGE', 'NoPackage']);
        const pkgCode = pick(item, ['PACKAGE_CODE', 'PackageCode']);
        const desc = pick(item, ['DESCRIPTIONS', 'CargoDescription', 'Description']);
        const scheduleB = pick(item, ['SCHEDULE_B', 'ScheduleB']);
        const kgs = pick(item, ['KGS', 'NetKgs']);
        const lbs = pick(item, ['LBS']);
        const cbm = pick(item, ['CBM']);
        const cft = pick(item, ['CFT']);

        return {
            marksAndNumbers: valueOrDash(`${marks}${containerNo ? ` / ${containerNo}` : ''}${seal ? ` / ${seal}` : ''}`),
            numberOfPackages: valueOrDash(`${pkg}${pkgCode ? ` ${pkgCode}` : ''}`),
            containerType: valueOrDash(`${contSize}${contType ? ` X ${contType}` : ''}`),
            description: valueOrDash(desc),
            scheduleB: valueOrDash(scheduleB),
            grossWeight: valueOrDash(`${kgs ? `${kgs} KGS` : ''}${kgs && lbs ? ' | ' : ''}${lbs ? `${lbs} LBS` : ''}`),
            measurement: valueOrDash(`${cbm ? `${cbm} CBM` : ''}${cbm && cft ? ' | ' : ''}${cft ? `${cft} CFT` : ''}`),
        };
    });
};

const decodeRouteBookingId = (id) => {
    if (id == null || id === '') return null;
    try {
        return atob(id);
    } catch {
        return id;
    }
};

const ImportHBLPrint = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const printRef = useRef();
    const { MasterCompanyDetails } = useSelector((state) => state.common);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? '[{}]');

    const { bookingId: bookingIdFromParam } = useParams();
    const { bookingId: bookingIdFromState } = location.state || {};
    const searchParams = new URLSearchParams(location.search);
    const bookingIdFromQuery = searchParams.get('bookingId');
    const bookingId =
        bookingIdFromState ||
        decodeRouteBookingId(bookingIdFromParam) ||
        bookingIdFromQuery ||
        null;

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            if (!bookingId || String(bookingId) === '0') {
                setLoading(false);
                return;
            }

            try {
                const [importBooking] = await Promise.all([
                    dispatch(getImportBookingDetails(bookingId, authUser?.companyId)).catch(() => null)
                ]);
                const importData = importBooking || {};
                setFormData({ ...importData });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [bookingId, authUser?.companyId, dispatch]);

    const handlePrint = () => {
        if (!printRef.current) return;
        const printContent = printRef.current.innerHTML;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const win = iframe.contentWindow;
        const iframeDoc = win.document;
        const styleTags = Array.from(document.querySelectorAll('style')).map((style) => style.outerHTML).join('');
        const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) => link.outerHTML).join('');

        let didPrint = false;
        const runPrint = () => {
            if (didPrint || !iframe.parentNode) return;
            didPrint = true;
            window.setTimeout(() => {
                try {
                    win.focus();
                    win.print();
                } catch {
                    if (iframe.parentNode) document.body.removeChild(iframe);
                }
            }, 300);
            win.onafterprint = () => {
                if (iframe.parentNode) document.body.removeChild(iframe);
            };
        };

        iframe.onload = runPrint;
        iframeDoc.open();
        iframeDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>HBL - ${pick(formData, ['HBLNumber', 'hblNumber', 'CarrierBookingNo']) || 'Document'}</title>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            ${linkTags}
            ${styleTags}
            <style>
                @page { size: A4; margin: 15mm; }
                body {
                    margin: 0;
                    padding: 0;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            </style>
        </head>
        <body class="bg-white">${printContent}</body>
        </html>
    `);
        iframeDoc.close();
        window.setTimeout(runPrint, 1200);
    };

    const handleDownload = async () => {
        if (!printRef.current || !formData) return;
        try {
            const downloadBtn = document.querySelector('[data-download-btn]');
            const originalText = downloadBtn?.textContent;
            if (downloadBtn) {
                downloadBtn.textContent = 'Generating PDF...';
                downloadBtn.disabled = true;
            }

            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const ratio = canvas.width / canvas.height;

            let width = pageWidth - 20;
            let height = width / ratio;
            if (height > pageHeight - 20) {
                height = pageHeight - 20;
                width = height * ratio;
            }
            pdf.addImage(imgData, 'PNG', (pageWidth - width) / 2, (pageHeight - height) / 2, width, height);
            pdf.save(`HBL_${pick(formData, ['HBLNumber', 'CarrierBookingNo']) || 'Document'}_${moment().format('YYYY-MM-DD')}.pdf`);

            if (downloadBtn) {
                downloadBtn.textContent = originalText;
                downloadBtn.disabled = false;
            }
        } catch {
            const downloadBtn = document.querySelector('[data-download-btn]');
            if (downloadBtn) {
                downloadBtn.textContent = 'Download PDF';
                downloadBtn.disabled = false;
            }
            alert('Failed to generate PDF. Please try printing instead.');
        }
    };

    const cargoRows = normalizeCargoRows(formData);
    const shipperName = valueOrDash(pick(formData, ['Shipper', 'shipper', 'ShipperName']));
    const shipperAddress = valueOrDash(pick(formData, ['ShipperAddress', 'shipperAddress', 'ShipperAdd', 'ShipperFullAddress']));
    const consigneeName = valueOrDash(pick(formData, ['Consignee', 'consignee', 'ConsigneeName']));
    const consigneeAddress = valueOrDash(pick(formData, ['ConsigneeAddress', 'consigneeAddress', 'ConsigneeAdd', 'ConsigneeFullAddress']));
    const notifyName = valueOrDash(pick(formData, ['Notify', 'notify', 'AlsoNotify', 'NotifyParty']));
    const notifyAddress = valueOrDash(
        pick(formData, ['NotifyAddress', 'notifyAddress', 'AlsoNotifyAddress', 'NotifyPartyAddress'])
    );
    const bookingNumber = valueOrDash(
        pick(formData, ['CarrierBookingNo', 'carrierBookingNumber', 'BookingNo', 'BookingNumber', 'FileRefNumber'])
    );
    const hblNumber = valueOrDash(pick(formData, ['HBLNumber', 'hblNumber', 'HouseBLNo', 'HouseBLNumber']));
    const customerReference = valueOrDash(
        pick(formData, ['CustomerPoNo', 'CustomerPONo', 'customerReference', 'FileRefNumber', 'ReferenceNo'])
    );
    const coloader = valueOrDash(pick(formData, ['Coloader', 'CoLoader', 'coLoader']));
    const forwardingAgent = valueOrDash(pick(formData, ['ForwardingAgent', 'forwardingAgent']));
    const forwardingAgentAddress = valueOrDash(
        pick(formData, ['ForwardingAgentAddress', 'forwardingAgentAddress'])
    );
    const pointOfOrigin = valueOrDash(
        pick(formData, ['CountryOfOrigin', 'countryOfOrigin', 'PointAndCountryOfOrigin', 'CountryOfDestination'])
    );
    const deliveryContact = valueOrDash(
        pick(formData, ['DeliveryAgent', 'deliveryAgent', 'Trucker', 'TruckerName'])
    );
    const deliveryAddress = valueOrDash(
        pick(formData, ['DeliveryAgentAddress', 'deliveryAgentAddress', 'TruckerAddress'])
    );
    const preCarriageBy = valueOrDash(
        pick(formData, ['PreCarrageBy', 'PreCarriageBy', 'preCarriageBy', 'CarrierName'])
    );
    const placeOfReceipt = valueOrDash(
        pick(formData, ['PalceOfReceipt', 'PlaceOfReceipt', 'placeOfReceipt', 'ourPlaceOfReceipt'])
    );
    const vesselName = pick(formData, ['VesselName', 'Vessel', 'vessel', 'MotherVessel']);
    const voyage = pick(formData, ['Voyage', 'voyage', 'VoyageNo']);
    const vesselVoyage = valueOrDash(`${vesselName || ''}${vesselName && voyage ? ' / ' : ''}${voyage || ''}`);
    const portOfLoading = valueOrDash(
        cleanPort(pick(formData, ['PortOfLoading', 'PortOfLoad', 'portOfLoading']))
    );
    const portOfDischarge = valueOrDash(
        cleanPort(pick(formData, ['PortOfDischarge', 'PortOfDestination', 'portOfDischarge']))
    );
    const placeOfDelivery = valueOrDash(
        pick(formData, ['PlaceOfDelivery', 'placeOfDelivery', 'FinalDestination', 'ourPlaceOfDelivery'])
    );
    const moveType = valueOrDash(
        pick(formData, ['MoveType', 'MoveType_TSC', 'moveType', 'ourMoveType'])
    );
    const loadingPierTerminal = valueOrDash(
        pick(formData, ['LoadingPierTerminal', 'LoadingLocationWarehouse', 'FreightPayable'])
    );
    const deliveredBy = valueOrDash(
        pick(formData, ['DeliveryAgent', 'Trucker', 'DeliveredBy', 'DeliveryBy'])
    );
    const checkedBy = valueOrDash(
        pick(formData, ['CreatedBy', 'ModifiedBy', 'PreparedBy', 'PreparedByName'])
    );
    const noOfOriginals = valueOrDash(pick(formData, ['NoOriginal', 'NoOfOriginals']));
    const isContainerized = String(pick(formData, ['IsContainerized', 'isContainerized'])).toLowerCase() === 'yes';

    const chargeSource = Array.isArray(formData?.BolArChargesData) && formData.BolArChargesData.length
        ? formData.BolArChargesData
        : Array.isArray(formData?.arCharges) && formData.arCharges.length
            ? formData.arCharges
            : [];

    const chargeRows = Object.values(
        chargeSource.reduce((acc, item) => {
            const description = valueOrDash(
                pick(item, ['DESCRIPTION', 'description', 'chargeDescription'])
            );
            if (!acc[description]) {
                acc[description] = { description, prepaid: 0, collect: 0 };
            }
            const ppcc = String(pick(item, ['PPCC', 'ppcc', 'paymentType'])).toUpperCase();
            const amount = Number(
                pick(item, ['AMOUNT', 'amount', 'sellAmount']) || 0
            );
            if (ppcc === 'P' || ppcc === 'PREPAID') {
                acc[description].prepaid += amount;
            } else {
                acc[description].collect += amount;
            }
            return acc;
        }, {})
    );

    const prepaidTotal = chargeRows.reduce((sum, item) => sum + item.prepaid, 0);
    const collectTotal = chargeRows.reduce((sum, item) => sum + item.collect, 0);
    const totalKgsDisplay = valueOrDash(pick(formData, ['totalkgs', 'TotalKgs']));
    const totalCbmDisplay = valueOrDash(pick(formData, ['totalcbm', 'TotalCbm']));

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading HBL data...</p>
                </div>
            </div>
        );
    }

    if (!formData) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center max-w-md px-4">
                    <p className="text-gray-800 font-medium">Could not load import HBL</p>
                    <p className="text-gray-600 text-sm mt-2">No data available for this booking ID.</p>
                    <button type="button" onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-[#1a2744] text-white rounded-lg text-sm">
                        Go back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-200 print:bg-white">
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleDownload}
                            data-download-btn
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300"
                        >
                            <Download className="w-4 h-4" />
                            Download PDF
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium"
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex justify-center py-6 print:py-0">
                <div ref={printRef} className="a4-page bg-white text-black shadow-xl print:shadow-none p-2">
                    <div className="hbl-sheet">
                        <div className="hbl-head">
                            <div className="hbl-logo-wrap">
                                <img src={MasterCompanyDetails?.[0]?.CompanyLogoDark} alt={MasterCompanyDetails?.[0]?.CompanyName || 'Company Logo'} className="hbl-logo" />
                            </div>
                            <div className="hbl-title">BILL OF LADING</div>
                        </div>

                        <div className="hbl-grid hbl-row-1">
                            <div className="hbl-cell hbl-left">
                                <div className="hbl-label">SHIPPER (Principal or Seller-licensee and address including ZIP Code)</div>
                                <div className="hbl-value">{shipperName}<br />{shipperAddress}</div>
                            </div>
                            <div className="hbl-right-wrap">
                                <div className="hbl-grid hbl-mini-row">
                                    <div className="hbl-cell"><div className="hbl-label">BOOKING NUMBER</div><div className="hbl-value">{bookingNumber}</div></div>
                                    <div className="hbl-cell"><div className="hbl-label">B/L NUMBER</div><div className="hbl-value">{hblNumber}</div></div>
                                </div>
                                <div className="hbl-cell hbl-grow"><div className="hbl-label">EXPORT REFERENCES</div><div className="hbl-value">CUST. REF. #: {customerReference}<br />{coloader !== '-' ? `CO-LOADER: ${coloader}` : ''}</div></div>
                            </div>
                        </div>

                        <div className="hbl-grid hbl-row-2">
                            <div className="hbl-cell hbl-left">
                                <div className="hbl-label">CONSIGNEE</div>
                                <div className="hbl-value">{consigneeName}<br />{consigneeAddress}</div>
                            </div>
                            <div className="hbl-right-wrap">
                                <div className="hbl-cell"><div className="hbl-label">FORWARDING AGENT (Name and address)</div><div className="hbl-value">{forwardingAgent}<br />{forwardingAgentAddress}</div></div>
                                <div className="hbl-cell"><div className="hbl-label">COUNTRY OF ORIGIN</div><div className="hbl-value">{pointOfOrigin}</div></div>
                            </div>
                        </div>

                        <div className="hbl-grid hbl-row-2">
                            <div className="hbl-cell hbl-left">
                                <div className="hbl-label">NOTIFY PARTY/INTERMEDIATE CONSIGNEE (Name and address)</div>
                                <div className="hbl-value">{notifyName}<br />{notifyAddress}</div>
                            </div>
                            <div className="hbl-right-wrap">
                                <div className="hbl-grid hbl-mini-row">
                                    <div className="hbl-cell"><div className="hbl-label">DESTINATION AGENT</div><div className="hbl-value">{deliveryContact}<br />{deliveryAddress}</div></div>
                                    <div className="hbl-cell"><div className="hbl-label">TYPE OF MOVE</div><div className="hbl-value">{moveType}</div></div>
                                </div>
                            </div>
                        </div>

                        <div className="hbl-grid hbl-row-3">
                            <div className="hbl-grid hbl-three">
                                <div className="hbl-cell"><div className="hbl-label">PRE-CARRIAGE BY</div><div className="hbl-value">{preCarriageBy}</div></div>
                                <div className="hbl-cell"><div className="hbl-label">PLACE OF RECEIPT BY PRE-CARRIER</div><div className="hbl-value">{placeOfReceipt}</div></div>
                                <div className="hbl-cell"><div className="hbl-label">OCEAN VESSEL</div><div className="hbl-value">{vesselVoyage}</div></div>
                            </div>
                            <div className="hbl-grid hbl-three hbl-three-right">
                                <div className="hbl-cell"><div className="hbl-label">PORT OF LOADING / EXPORT</div><div className="hbl-value">{portOfLoading}</div></div>
                                <div className="hbl-cell"><div className="hbl-label">LOADING PIER / TERMINAL</div><div className="hbl-value">{loadingPierTerminal}</div></div>
                                <div className="hbl-cell"><div className="hbl-label">NO. OF ORIGINAL(S)</div><div className="hbl-value text-center">{noOfOriginals}</div></div>
                            </div>
                        </div>

                        <div className="hbl-grid hbl-row-4">
                            <div className="hbl-cell"><div className="hbl-label">PORT OF DISCHARGE</div><div className="hbl-value">{portOfDischarge}</div></div>
                            <div className="hbl-cell"><div className="hbl-label">PLACE OF DELIVERY BY ON-CARRIER</div><div className="hbl-value">{placeOfDelivery}</div></div>
                            <div className="hbl-cell"><div className="hbl-label">FOR DELIVERY PLEASE CONTACT</div><div className="hbl-value">{deliveryContact}</div></div>
                            <div className="hbl-cell">
                                <div className="hbl-label">CONTAINERIZED (Vessel Only)</div>
                                <div className="hbl-check-row">
                                    <span className={`hbl-box ${isContainerized ? 'checked' : ''}`}>{isContainerized ? 'X' : ''}</span> Yes
                                    <span className={`hbl-box ${!isContainerized ? 'checked' : ''}`}>{!isContainerized ? 'X' : ''}</span> No
                                </div>
                            </div>
                        </div>

                        <div className="hbl-lower-section">
                            <div className="hbl-cargo">
                                <table className="w-full text-[8px] border-collapse">
                                    <thead>
                                        <tr className="uppercase">
                                            <th className="w-[22%] p-1 border-r border-[#7db4ff] border-b border-[#7db4ff]">
                                                Marks and Numbers
                                                <div className="normal-case text-[7px]">Container#/ Seal#</div>
                                            </th>
                                            <th className="w-[15%] p-1 border-r border-[#7db4ff] border-b border-[#7db4ff]">Container Type</th>
                                            <th className="w-[36%] p-1 border-r border-[#7db4ff] border-b border-[#7db4ff]">Description of Commodities</th>
                                            <th className="w-[14%] p-1 border-r border-[#7db4ff] border-b border-[#7db4ff]">Gross Weight (Kilos)</th>
                                            <th className="w-[13%] p-1 border-b border-[#7db4ff]">Measurement</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            // Total table rows to show (you can adjust the number as per visual requirement)
                                            const VISIBLE_ROWS = 10;
                                            // Always render at least VISIBLE_ROWS, filling with blanks if needed
                                            const rows =
                                                cargoRows && cargoRows.length > 0
                                                    ? cargoRows.slice(0, VISIBLE_ROWS)
                                                    : [];
                                            // Number of blank rows needed
                                            const blankRowsCount = VISIBLE_ROWS - rows.length;
                                            return (
                                                <>
                                                    {rows.map((item, index) => (
                                                        <tr key={index} className="align-top">
                                                            <td className="p-1 border-r border-[#7db4ff]">{item.marksAndNumbers}</td>
                                                            <td className="p-1 border-r border-[#7db4ff]">{item.containerType}</td>
                                                            <td className="p-1 border-r border-[#7db4ff]">
                                                                {item.description}
                                                                {item.scheduleB !== '-' ? <><br />SCHEDULE B: {item.scheduleB}</> : null}
                                                            </td>
                                                            <td className="p-1 border-r border-[#7db4ff]">{item.grossWeight}</td>
                                                            <td className="p-1">{item.measurement}</td>
                                                        </tr>
                                                    ))}
                                                    {[...Array(blankRowsCount > 0 ? blankRowsCount : 0)].map((_, idx) => (
                                                        <tr key={`blank-${idx}`} className="align-top">
                                                            <td className="p-1 border-r border-[#7db4ff]">&nbsp;</td>
                                                            <td className="p-1 border-r border-[#7db4ff]">&nbsp;</td>
                                                            <td className="p-1 border-r border-[#7db4ff]">&nbsp;</td>
                                                            <td className="p-1 border-r border-[#7db4ff]">&nbsp;</td>
                                                            <td className="p-1">&nbsp;</td>
                                                        </tr>
                                                    ))}
                                                </>
                                            );
                                        })()}

                                        <tr className="h-[24px] border-t border-[#7db4ff]">
                                            <td className="border-r border-[#7db4ff]"></td>
                                            <td className="border-r border-[#7db4ff] text-center font-semibold">TOTAL</td>
                                            <td className="border-r border-[#7db4ff]"></td>
                                            <td className="border-r border-[#7db4ff] text-center font-semibold">{totalKgsDisplay}</td>
                                            <td className="text-center font-semibold">{totalCbmDisplay}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div className="hbl-original-watermark">ORIGINAL</div>
                            </div>

                            <div className="hbl-policy">
                                Carrier Has a policy against paym ent, solicitation, or receipt of any rebate, directly or indirectly, which would be unlawful under the United State Shipping Act, 1984 as am ended. DECLARED
                                VALUE _______________________________ READ CLAUSE 22 HEREOF CONCERNI NG EXTRA FREI GHT AND CARRI ER'S LI MI TATI ONS OF LI ABI LI TY.
                            </div>

                            <div className="hbl-grid hbl-bottom">
                                <div className="hbl-charge-box">
                                    <table className="w-full text-[8px] border-collapse hbl-charge-table">
                                        <thead>
                                            <tr>
                                                <th colSpan={3} className="p-1 border-b border-[#7db4ff] uppercase text-center font-bold text-[#2563eb]">
                                                    Freight Rates, Charges, Weights and/or Measurement
                                                </th>
                                            </tr>
                                            <tr className="uppercase">
                                                <th className="w-[50%] p-1 border-r border-b border-[#7db4ff] text-center font-bold text-[#2563eb]">Subject To Correction</th>
                                                <th className="w-[25%] p-1 border-r border-b border-[#7db4ff] text-center font-bold text-[#2563eb]">Prepaid</th>
                                                <th className="w-[25%] p-1 border-b border-[#7db4ff] text-center font-bold text-[#2563eb]">Collect</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                // Always render at least 12 lines, but display all chargeRows if more; if none, 12 blank rows
                                                const MIN_ROWS = 12;
                                                let rowsToShow = [];

                                                if (Array.isArray(chargeRows) && chargeRows.length > 0) {
                                                    rowsToShow = [...chargeRows];
                                                    if (chargeRows.length < MIN_ROWS) {
                                                        for (let i = chargeRows.length; i < MIN_ROWS; i++) {
                                                            rowsToShow.push({});
                                                        }
                                                    }
                                                } else {
                                                    // No charges, just show 12 blank rows
                                                    rowsToShow = Array.from({ length: MIN_ROWS }, () => ({}));
                                                }

                                                return rowsToShow.map((row, idx) => (
                                                    <tr key={idx}>
                                                        <td className="p-1 border-r border-[#7db4ff] text-[#1e3a8a]">
                                                            {row.description || ""}
                                                        </td>
                                                        <td className="p-1 border-r border-[#7db4ff] text-right text-[#1e3a8a]">
                                                            {row.prepaid != null && !isNaN(Number(row.prepaid)) && row.prepaid !== "" ? Number(row.prepaid).toFixed(2) : ""}
                                                        </td>
                                                        <td className="p-1 text-right text-[#1e3a8a]">
                                                            {row.collect != null && !isNaN(Number(row.collect)) && row.collect !== "" ? Number(row.collect).toFixed(2) : ""}
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}


                                            <tr className="border-t border-[#7db4ff]">
                                                <td className="p-1 border-r border-[#7db4ff] font-bold text-[#2563eb] uppercase">Grand Total</td>
                                                <td className="p-1 border-r border-[#7db4ff] text-right font-semibold text-[#1e3a8a]">{prepaidTotal ? prepaidTotal.toFixed(2) : ''}</td>
                                                <td className="p-1 text-right font-semibold text-[#1e3a8a]">{collectTotal ? collectTotal.toFixed(2) : ''}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <table className="w-full text-[8px] border border-[#7db4ff] hbl-sign-table" style={{ borderCollapse: "collapse" }}>
                                    <tbody>
                                        <tr>
                                            <td colSpan={4} className="border-b border-[#7db4ff] p-2 hbl-sign-terms">
                                                <div>
                                                    <strong>a)</strong> Except as otherwise provided in this Bill of Lading, or required by compulsorily applicable legislation, the Carrier's liability, if any,
                                                    shall be determined on the basis of a value of $500 per package or, in the case of cargo not shipped in packages, per customary
                                                    freight unit, unless the nature of the cargo and valuation higher than $500 per package or customary freight unit shall have been
                                                    declared by the Merchant before shipment and inserted in this Bill of Lading, and extra freight paid if required. In such case, if the
                                                    actual value of the cargo per package or customary freight unit shall exceed such declared value, the value shall nevertheless be
                                                    deemed to be the declared value and the Carrier's liability, if any, shall not exceed the declared value. For the purpose of determining
                                                    Carrier's liability under this Section 22, package shall mean the packages or other shipping units enumerated on the face of
                                                    the Bill of Lading.
                                                </div>
                                                <div className="mt-2">
                                                    <strong>b)</strong> Where compulsorily applicable legislation provides a limitation of less than $500 per package or shipping unit, such lesser
                                                    limitation shall apply and nothing herein contained shall be construed as a waiver of a limitation of less than $500. Further, where a
                                                    lesser monetary limitation is applicable, such as during handling by a Participating Carrier or independent contractor and damage
                                                    occurs during its or their period of care, custody, control and/or responsibility, the Carrier shall be entitled to avail itself of such
                                                    lesser limitation.
                                                </div>
                                                <div className="mt-2">
                                                    If the Merchant desires to be covered for a valuation in excess of said US$500 per package or customary freight unit or any
                                                    other applicable limitation, the Merchant must so stipulate in this Bill of Lading and such additional liability only will be
                                                    assumed by the Carrier upon payment of the Carrier's ad valorem freight charge, Declared Cargo Value US$___________. If
                                                    Merchant enters a value, Carrier's limitation of liability shall not apply and the ad valorem rate will be charged.
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-1 font-semibold w-[14%] border-b border-r border-[#7db4ff]">DATED AT</td>
                                            <td className="p-1 w-[36%] border-b border-r border-[#7db4ff]">{portOfLoading}</td>
                                            <td className="p-1 font-semibold w-[14%] border-b border-r border-[#7db4ff]">SIGNATURE</td>
                                            <td className="p-1 w-[36%] border-b border-[#7db4ff]"></td>
                                        </tr>
                                        <tr>
                                            <td className="p-1 font-semibold border-b border-r border-[#7db4ff]">BY</td>
                                            <td className="p-1 border-b border-r border-[#7db4ff]">{MasterCompanyDetails?.[0]?.CompanyName || ''} As Carrier</td>
                                            <td className="p-1 font-semibold border-b border-r border-[#7db4ff]">B/L No.</td>
                                            <td className="p-1 border-b border-[#7db4ff]">{hblNumber}</td>
                                        </tr>
                                        <tr>
                                            <td className="p-1 font-semibold border-r border-[#7db4ff]">MONTH</td>
                                            <td className="p-1 font-semibold border-r border-[#7db4ff]">DAY</td>
                                            <td className="p-1 font-semibold border-r border-[#7db4ff]">YEAR</td>
                                            <td className="p-1">{formatDate(pick(formData, ['CreatedOn', 'createdOn']))}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                

                            </div>
                            
                        </div>
                        
                    </div>
                     {/* ── FOOTER — always at bottom of A4 page ── */}
                    <div className="text-[8px] text-muted-foreground mt-1">
                        Powered by {MasterCompanyDetails?.[0]?.PoweredBy || 'www.infox.com'}
                    </div>
                </div>
            </div>
            

            <style>
                {`
                    .a4-page {
                        width: 210mm;
                        min-height: 297mm;
                        box-sizing: border-box;
                        font-size: 10px;
                        background: #ffffff !important;
                        color: #1e3a8a !important;
                        font-family: Arial, sans-serif;
                    }
                    .a4-page, .a4-page * { box-sizing: border-box; }
                    .hbl-sheet, .hbl-sheet * { color: #1e3a8a; }
                    .hbl-sheet .hbl-label, .hbl-sheet .hbl-label * { color: #2563eb !important; }
                    .hbl-sheet .hbl-title { color: #1d4ed8 !important; }
                    .a4-page .dock-title { color: #38bdf8 !important; }
                    .a4-page img { filter: none !important; }
                    .hbl-sheet {
                        width: 100%;
                        position: relative;
                        border: 1px solid #7db4ff;
                        box-sizing: border-box;
                        outline: 1px solid #7db4ff;
                        outline-offset: -1px;
                    }
                    .hbl-head { display: flex; justify-content: space-between; align-items: center; padding: 4px 6px 2px; }
                    .hbl-logo { height: 48px; width: auto; object-fit: contain; }
                    .hbl-title { font-size: 16px; font-weight: 600; line-height: 1; letter-spacing: 0.3px; color: #1d4ed8; }
                    .hbl-grid { display: grid; border-top: 1px solid #7db4ff; }
                    .hbl-cell { border-right: 1px solid #7db4ff; padding: 2px 4px; min-height: 20px; box-sizing: border-box; }
                    .hbl-cell:last-child { border-right: none; }
                    .hbl-label { font-size: 9px; font-weight: 700; color: #2563eb; line-height: 1.1; text-transform: uppercase; }
                    .hbl-label.center { text-align: center; }
                    .hbl-value { font-size: 9px; color: #1e3a8a; line-height: 1.2; white-space: pre-line; }
                    .hbl-row-1 { grid-template-columns: 52% 48%; }
                    .hbl-row-2 { grid-template-columns: 52% 48%; }
                    .hbl-right-wrap { border-left: 1px solid #7db4ff; }
                    /* Nested grid: skip duplicate top line (parent row already has border-top) */
                    .hbl-right-wrap > .hbl-mini-row.hbl-grid { border-top: none; }
                    /* Separator between Booking/B-L row and Export References (row 1) */
                    .hbl-right-wrap > .hbl-mini-row + .hbl-cell { border-top: 1px solid #7db4ff; }
                    .hbl-mini-row { grid-template-columns: 65% 35%; }
                    .hbl-grow { min-height: 54px; }
                    .hbl-row-3 { grid-template-columns: 52% 48%; }
                    .hbl-three { grid-template-columns: 1fr 1fr 1fr; border-top: none; }
                    .hbl-three-right { border-left: 1px solid #7db4ff; }
                    .hbl-row-4 { grid-template-columns: 25% 25% 25% 25%; }
                    .hbl-check-row { display: flex; align-items: center; gap: 8px; font-size: 8px; margin-top: 2px; }
                    .hbl-box { width: 12px; height: 12px; border: 1px solid #2563eb; display: inline-flex; align-items: center; justify-content: center; font-size: 8px; font-weight: 700; color: #2563eb; }
                    .hbl-lower-section {
                        border-left: 1px solid #7db4ff;
                        border-right: 1px solid #7db4ff;
                        border-bottom: 1px solid #7db4ff;
                    }
                    .hbl-cargo { border-top: 1px solid #7db4ff; position: relative; }
                    .hbl-cargo-head { grid-template-columns: 22% 15% 36% 14% 13%; }
                    .hbl-cargo-body { min-height: 220px; position: relative; border-top: 1px solid #7db4ff; }
                    .hbl-cargo-row { grid-template-columns: 22% 15% 36% 14% 13%; border-top: none; }
                    .hbl-cargo-row .hbl-cell { min-height: 34px; border-right: 1px solid #7db4ff; }
                    .hbl-cargo-head .hbl-cell:last-child,
                    .hbl-cargo-row .hbl-cell:last-child { border-right: 1px solid #7db4ff; }
                    .hbl-original-watermark {
                        position: absolute;
                        left: 92px;
                        top: 90px;
                        font-size: 80px;
                        color: #9ca3af;
                        opacity: 0.5;
                        pointer-events: none;
                    }
                    .hbl-policy { border-top: 1px solid #7db4ff; border-bottom: 1px solid #7db4ff; padding: 2px 4px; font-size: 6px; color: #2563eb; }
                    .hbl-bottom { grid-template-columns: 47% 53%; }
                    .hbl-charge-box, .hbl-sign-box { min-height: 170px; height: 100%; }
                    .hbl-charge-box { border-right: 1px solid #7db4ff; }
                    .hbl-charge-table, .hbl-sign-table { height: 100%; }
                    .hbl-charge-head, .hbl-charge-row, .hbl-charge-total { grid-template-columns: 50% 25% 25%; }
                    .hbl-charge-body { min-height: 120px; border-top: 1px solid #7db4ff; }
                    .hbl-charge-row { border-top: none; }
                    .hbl-charge-row .hbl-cell, .hbl-charge-total .hbl-cell, .hbl-charge-head .hbl-cell { min-height: 14px; }
                    .hbl-charge-total { border-top: 1px solid #7db4ff; }
                    .hbl-sign-box { padding: 0; }
                    .hbl-sign-terms { font-size: 6px; line-height: 1.25; min-height: 92px; padding: 2px 4px; border-bottom: 1px solid #7db4ff; color: #1e3a8a; }
                    .hbl-sign-row { display: grid; grid-template-columns: 12% 38% 18% 32%; align-items: end; gap: 4px; margin-top: 0; font-size: 8px; padding: 2px 4px; border-bottom: 1px solid #7db4ff; }
                    .hbl-sign-row.small { grid-template-columns: repeat(4, 1fr); text-align: center; font-size: 7px; margin-top: 0; border-bottom: 0; }
                    .hbl-line { border-bottom: 1px solid #2563eb; min-height: 10px; display: inline-flex; align-items: end; font-size: 8px; }
                    .hbl-lower-section table { border-color: #7db4ff !important; }
                    .hbl-lower-section th { border-color: #7db4ff !important; color: #2563eb !important; }
                    .hbl-lower-section td { border-color: #7db4ff !important; color: #1e3a8a !important; }
                    @media screen { .a4-page { margin: 0 auto; } }
                    @media print {
                        @page { size: A4; margin: 10mm; }
                        html, body { width: 210mm; height: 297mm; }
                        body {
                            margin: 0;
                            padding: 0;
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                        .a4-page {
                            width: 100%;
                            min-height: auto;
                            padding: 0;
                            margin: 0;
                            box-shadow: none !important;
                            page-break-inside: avoid;
                            overflow: hidden;
                            background: #ffffff !important;
                            color: #1e3a8a !important;
                        }
                        .print\\:hidden { display: none !important; }
                        .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
                        .print\\:shadow-none { box-shadow: none !important; }
                        .print\\:bg-white { background: white !important; }
                    }
                `}
            </style>
        </div>
    );
};

export default ImportHBLPrint;
